import {
  Modal,
  message,
  Row,
  Col,
  Button,
  Table,
  Input,
  FloatButton,
  Spin,
  notification,
  Form,
  Space,
  Select,
  Calendar,
  Badge,
  Segmented,
} from "antd";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import axios from "axios";
import {
  HEADERS_EXPORT_BEN,
  POST_EMS_AGREEMENT,
  POST_LOAN_DB2_V2,
} from "../../../API/apiUrls";
import dayjs from "dayjs";
import {
  RestOutlined,
  SearchOutlined,
  FileExcelOutlined,
  SaveOutlined,
  InfoCircleOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import { findParcelDuplicates } from "../../../../hook/CheckDuplicateParcel";
import { excelForPostOffice } from "../report/ExportPostOffice";
import ThaiDatePicker from "../../../../hook/ThaiDatePicker";
import { Link } from "react-router-dom";
import BillAfterCourtOldData from "./BillAfterCourtOldData";

// ── AutoFocusInput ─────────────────────────────────────────────
const AutoFocusInput = React.memo(
  ({ record, index, onComplete, isDuplicate, onParcelChange }) => {
    return (
      <Form.Item
        name={["rows", record.key, "parcelNo"]}
        style={{ margin: 0 }}
        rules={[{ len: 13, message: "กรอกให้ครบ 13 หลัก" }]}
        getValueFromEvent={(e) => e.target.value}
        validateStatus={isDuplicate ? "error" : ""}
        help={isDuplicate ? `ซ้ำกับ ${isDuplicate}` : ""}
      >
        <Input
          id={`input-ems-${index}`}
          placeholder="กรอกเลข EMS"
          maxLength={13}
          style={{
            textAlign: "center",
            borderColor: isDuplicate ? "red" : undefined,
          }}
          onChange={(e) => {
            const v = e.target.value.trim();
            onParcelChange(record.key, v); // ✅ update ทันที + mark dirty
            if (v.length === 13) onComplete(index);
          }}
          onPressEnter={() => onComplete(index)}
        />
      </Form.Item>
    );
  },
);

// ── Main Component ─────────────────────────────────────────────
const AddEmsAndExport = ({ open, close, dataDefault, onRefresh }) => {
  const { Search } = Input;
  const [loading, setLoading] = useState(false);
  const [arrayTable, setArrayTable] = useState([]);
  const [arrData, setArrData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [datePicker, setDatePicker] = useState(null);
  const [btnCheckCalendar, setBtnCheckCalendar] = useState(false);
  const [calendarMode, setCalendarMode] = useState("month");
  const [calendarValue, setCalendarValue] = useState(dayjs());
  const [form] = Form.useForm();
  const scrollRef = useRef(null);
  const [dirtyKeys, setDirtyKeys] = useState(new Set()); // ✅ track แถวที่ถูก key ใหม่
  const [isModal, setIsModal] = useState(false);
  const [dataRecord, setDataRecord] = useState(false);

  // ✅ calendarPoint จาก roundDate ของ dataDefault
  const calendarPoint = useMemo(() => {
    if (!dataDefault?.length) return [];
    return dataDefault.filter((item) => item.roundDate);
  }, [dataDefault]);

  // ✅ sync Form เมื่อ arrayTable เปลี่ยน
  useEffect(() => {
    const rowsObj = arrayTable.reduce((acc, r) => {
      acc[r.key] = { parcelNo: r.parcelNo ?? "" };
      return acc;
    }, {});
    form.setFieldsValue({ rows: rowsObj });
  }, [arrayTable, form]);

  useEffect(() => {
    if (datePicker && btnCheckCalendar) {
      loadByDate(datePicker);
    }
  }, [dataDefault]);

  // ✅ focus ช่องถัดไป ถ้าไม่มีให้วนกลับช่องแรก
  const handleInputComplete = useCallback((index) => {
    const next = document.getElementById(`input-ems-${index + 1}`);
    if (next) {
      next.focus();
      next.select(); // ✅ select ทั้งหมดเพื่อพิมพ์ทับได้ทันที
    } else {
      // ถึงช่องสุดท้ายแล้ว → กลับช่องแรก
      const first = document.getElementById(`input-ems-0`);
      if (first) first.focus();
    }
  }, []);

  const handleParcelChange = useCallback((key, value) => {
    setArrayTable((prev) =>
      prev.map((r) => (r.key === key ? { ...r, parcelNo: value } : r)),
    );
    setDirtyKeys((prev) => {
      const next = new Set(prev);
      if (value)
        next.add(key); // มีค่า → dirty
      else next.delete(key); // ลบค่า → ไม่ dirty
      return next;
    });
  }, []);

  // ── หา duplicate ─────────────────────────────────────────────
  const duplicateMap = useMemo(() => {
    const map = {}; // { parcelNo: [contno, contno, ...] }

    arrayTable.forEach((r) => {
      if (!r.parcelNo || r.parcelNo.length !== 13) return;
      if (!map[r.parcelNo]) map[r.parcelNo] = [];
      map[r.parcelNo].push(r.newContno || r.CONTNO);
    });

    // เอาเฉพาะที่ซ้ำ (มากกว่า 1)
    const result = {};
    Object.entries(map).forEach(([parcelNo, contracts]) => {
      if (contracts.length > 1) result[parcelNo] = contracts;
    });

    return result;
  }, [arrayTable]);

  // ✅ mirror ค่าลง state ทันทีเมื่อ Form เปลี่ยน
  const handleFormValuesChange = (changedValues) => {
    const rows = changedValues?.rows;
    if (!rows) return;
    Object.entries(rows).forEach(([key, fields]) => {
      if (fields?.parcelNo !== undefined) {
        const numKey = isNaN(key) ? key : Number(key);
        setArrayTable((prev) =>
          prev.map((r) =>
            r.key === numKey ? { ...r, parcelNo: fields.parcelNo } : r,
          ),
        );
      }
    });
  };

  const handleCancel = () => close(false);

  const loadByDate = useCallback(
    (date) => {
      if (!date || !dataDefault?.length) return;
      const filtered = dataDefault
        .filter(
          (item) =>
            item.roundDate && dayjs(item.roundDate).isSame(dayjs(date), "day"),
        )
        .map((item, i) => ({ ...item, key: item.idJudge ?? i }));
      setArrData(filtered);
      setArrayTable(filtered);
      setDirtyKeys(new Set()); // ✅ reset ทุกครั้งที่ load ใหม่
    },
    [dataDefault],
  );

  // ── Search ────────────────────────────────────────────────────
  const onSearch = useCallback(
    (value) => {
      const keyword = value?.trim().toLowerCase();
      if (!keyword) {
        setArrayTable(arrData);
        return;
      }
      const result = arrData.filter((item) => {
        const fullName =
          `${item.SNAM ?? ""}${item.NAME1 ?? ""} ${item.NAME2 ?? ""}`.toLowerCase();
        return (
          item.CONTNO?.toLowerCase().includes(keyword) ||
          item.newContno?.toLowerCase().includes(keyword) ||
          fullName.includes(keyword) ||
          item.parcelNo?.toLowerCase().includes(keyword)
        );
      });
      setArrayTable(result);
    },
    [arrData],
  );

  // ── Save ──────────────────────────────────────────────────────
  const confirmInsert = useCallback(() => {
    setLoading(true);
    const { sameField } = findParcelDuplicates(arrayTable);

    if (sameField.length) {
      notification.error({
        message: "ไม่สามารถบันทึกได้ !",
        description: (
          <div>
            <div>มีข้อมูล EMS ซ้ำกัน กรุณาตรวจสอบ !</div>
            <ul style={{ margin: "8px 0 0", paddingLeft: 20, color: "red" }}>
              {[...new Set(sameField.map((d) => d.value))].map((v, i) => (
                <li key={i}>{v}</li>
              ))}
            </ul>
          </div>
        ),
        duration: 10,
      });
      setLoading(false);
      return;
    }

    // ✅ ส่งเฉพาะแถวที่ถูก key ใหม่ และครบ 13 หลัก
    // const payload = arrayTable.filter(
    //   (r) => dirtyKeys.has(r.key) && r.parcelNo?.length === 13,
    // );
    const payload = arrayTable.filter((r) => r.parcelNo?.length === 13);

    if (!payload.length) {
      message.warning("ไม่มีข้อมูล EMS ที่พร้อมบันทึก");
      setLoading(false);
      return;
    }

    const sendPayload = payload.map((r) => ({
      id: r.idJudge,
      parcelNo: r.parcelNo,
    }));

    putDataParcel(sendPayload);
  }, [arrayTable, dirtyKeys]);

  const putDataParcel = async (dataParcel) => {
    try {
      const res = await axios.post(POST_EMS_AGREEMENT, dataParcel, {
        headers: HEADERS_EXPORT_BEN,
      });
      if (res.status === 200) {
        message.success("อัพเดทเลขไปรษณีย์สำเร็จ");
        setDirtyKeys(new Set());
        await onRefresh?.(); // ✅ parent fetch ใหม่ → dataDefault เปลี่ยน → useEffect kick
      } else {
        message.success(`สำเร็จ (รหัส ${res.status})`);
      }
    } catch (error) {
      const status = error?.response?.status;
      if (status === 500) {
        message.error("ข้อมูลเลขไปรษณีย์ซ้ำกับข้อมูลเดิม !!");
      } else {
        message.error(`ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}`);
      }
    } finally {
      setLoading(false);
    }
  };
  const handleExportExcel = async () => {
    setLoading(true);
    const useData = selectedRows.length > 0 ? selectedRows : arrayTable;

    try {
      // ── 1. groupby SCHEMA ─────────────────────────────────────
      const groupedBySchema = useData.reduce((acc, item) => {
        if (!item.CONTNO || !item.SCHEMA) return acc;
        if (!acc[item.SCHEMA]) acc[item.SCHEMA] = [];
        acc[item.SCHEMA].push(`'${item.CONTNO}'`);
        return acc;
      }, {});

      const schemaPayload = Object.entries(groupedBySchema).reduce(
        (acc, [schema, arr]) => {
          acc[schema] = arr.join(",");
          return acc;
        },
        {},
      );

      let db2Map = {};
      try {
        const db2Res = await axios.post(POST_LOAN_DB2_V2, schemaPayload, {
          headers: HEADERS_EXPORT_BEN,
        });

        const db2Data = Array.isArray(db2Res.data)
          ? db2Res.data
          : Array.isArray(db2Res.data?.data)
            ? db2Res.data.data
            : [];

        db2Data.forEach((item) => {
          const contno = item?.LOAN?.CONTNO;
          if (!contno) return;

          // ✅ หา address ที่ ADDRNO === "1"
          const mainAddress = item.CUSTOMER?.ADDRESS?.find(
            (a) => a.ADDRNO === "1",
          );

          db2Map[contno] = {
            ADDRESS: item.CUSTOMER?.ADDRESS ?? [],
            GUARANTORS: item.GUARANTORS ?? [],
            LOCAT: item.LOAN?.LOCAT,
            OFFIC: item.CUSTOMER?.OFFIC,
            OCCUP: item.CUSTOMER?.OCCUP,
            ZIP: mainAddress?.ZIP ?? "",
            ADDR1: mainAddress?.ADDR1 ?? "",
            TUMB: mainAddress?.TUMB ?? "",
            AUMPDES: mainAddress?.AUMPDES ?? "",
            PROVDES: mainAddress?.PROVDES ?? "",
            TELP: mainAddress?.TELP ?? "",
          };
        });
      } catch (e) {
        console.warn("DB2 failed:", e);
      }

      // ── 3. merge ข้อมูล ───────────────────────────────────────
      const mergedData = useData.map((item) => ({
        ...item,
        ...(db2Map[item.CONTNO] ?? {}),
      }));

      // ── 4. export ─────────────────────────────────────────────
      await excelForPostOffice(mergedData, 1);
      message.success("โหลด Excel สำเร็จ");
    } catch (e) {
      message.error(`พิมพ์ไม่สำเร็จ: ${e?.message || "Unknown error"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setArrayTable([]);
    setArrData([]);
    form.resetFields();
  };

  const columns = useMemo(
    () => [
      {
        title: "ลำดับ",
        key: "index",
        align: "center",
        width: 60,
        render: (_, __, i) => i + 1,
      },
      {
        title: "เลขที่สัญญา",
        key: "CONTNO",
        align: "center",
        render: (_, r) => (
          <span
            style={{ color: "#1677ff", cursor: "pointer", fontWeight: 600 }}
            onClick={() => {
              setIsModal(true);
              setDataRecord(r);
            }}
          >
            {r.newContno || r.CONTNO || "-"}
          </span>
        ),
      },
      {
        title: "ชื่อ-นามสกุล",
        key: "name",
        align: "center",
        render: (_, r) =>
          `${r.SNAM || ""}${r.NAME1 || ""} ${r.NAME2 || ""}`.trim() || "-",
      },
      {
        title: "รอบส่ง",
        key: "roundDate",
        align: "center",
        render: (_, r) =>
          r.roundDate ? dayjs(r.roundDate).format("DD/MM/YYYY") : "-",
      },
      {
        title: "EMS จดหมาย",
        key: "parcelNo",
        align: "center",
        width: 220,
        render: (_, r, i) => {
          const dupContracts = r.parcelNo ? duplicateMap[r.parcelNo] : null;
          const isDuplicate = dupContracts
            ? dupContracts
                .filter((c) => c !== (r.newContno || r.CONTNO))
                .join(", ")
            : null;

          return (
            <AutoFocusInput
              record={r}
              index={i}
              onComplete={handleInputComplete}
              isDuplicate={isDuplicate}
              onParcelChange={handleParcelChange} // ✅ ส่ง callback
            />
          );
        },
      },
    ],
    [handleInputComplete, duplicateMap, handleParcelChange],
  );

  const dateCellRender = (value) => {
    const list = calendarPoint.filter((item) =>
      dayjs(item.roundDate).isSame(value, "day"),
    );
    if (!list.length) return null;
    return (
      <li style={{ listStyle: "none", textAlign: "center" }}>
        <Badge
          status="success"
          text={
            <Link
              onClick={() => {
                setCalendarValue(value);
                setDatePicker(value);
                loadByDate(value);
                setBtnCheckCalendar(true);
              }}
            >
              <InfoCircleOutlined style={{ color: "blue", marginRight: 4 }} />
              {list.length} รายการ
            </Link>
          }
        />
      </li>
    );
  };

  const monthCellRender = (value) => {
    const count = calendarPoint.filter((item) =>
      dayjs(item.roundDate).isSame(value, "month"),
    ).length;
    return count > 0 ? (
      <div style={{ textAlign: "center", paddingTop: 8 }}>
        <Badge count={count} style={{ backgroundColor: "blue" }} />
      </div>
    ) : null;
  };

  const cellRender = (current, info) => {
    if (info.type === "date") return dateCellRender(current);
    if (info.type === "month") return monthCellRender(current);
    return info.originNode;
  };

  const handleSelect = (value, { source }) => {
    if (calendarMode === "year") {
      setCalendarMode("month");
      setCalendarValue(value);
      return;
    }

    // ✅ เฉพาะกด date จริงๆ ไม่ใช่ navigate header
    if (source === "date") {
      setCalendarValue(value);
      setDatePicker(value);
      loadByDate(value);
      setBtnCheckCalendar(true);
    } else {
      setCalendarValue(value);
    }
  };

  return (
    <Spin spinning={loading} size="large" tip="Loading...">
      <Modal
        title={
          <span style={{ fontSize: 16, fontWeight: 400 }}>ส่ง EMS (ทำยอม)</span>
        }
        open={open}
        onCancel={handleCancel}
        width={btnCheckCalendar ? "auto" : 850}
        footer={null}
        keyboard
        maskClosable={false}
        centered
        styles={{ body: { padding: "24px 32px" } }}
      >
        {btnCheckCalendar ? (
          <div ref={scrollRef} style={{ maxHeight: 800, overflow: "auto" }}>
            <Row justify="center" style={{ marginBottom: 16 }}>
              <Space wrap size={8}>
                <Button
                  icon={<ArrowLeftOutlined />}
                  size="large"
                  type="primary"
                  ghost
                  disabled={loading}
                  onClick={() => {
                    setBtnCheckCalendar(false);
                    handleClear();
                  }}
                >
                  ย้อนกลับ
                </Button>
                <ThaiDatePicker value={datePicker} onChange={setDatePicker} />
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  size="large"
                  style={{ borderRadius: 8 }}
                  disabled={loading}
                  onClick={() => loadByDate(datePicker)}
                >
                  ค้นหา
                </Button>
                <Button
                  icon={<RestOutlined />}
                  size="large"
                  danger
                  style={{ borderRadius: 8 }}
                  disabled={loading}
                  onClick={handleClear}
                >
                  ล้างข้อมูล
                </Button>
              </Space>
            </Row>

            <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 8 }}>
              <Col xs={24} md={12}>
                <Search
                  placeholder="ค้นหาสัญญา / ชื่อ / EMS"
                  enterButton="ค้นหา"
                  allowClear
                  size="large"
                  style={{ maxWidth: 320 }}
                  onSearch={onSearch}
                  onChange={(e) => {
                    if (!e.target.value) setArrayTable(arrData);
                    else onSearch(e.target.value);
                  }}
                />
              </Col>
              <Col xs={24} md={12} style={{ textAlign: "end" }}>
                <Space size={8} wrap>
                  <Button
                    type="primary"
                    ghost
                    icon={<SaveOutlined />}
                    loading={loading}
                    style={{ borderRadius: 8, fontWeight: 600 }}
                    onClick={() => form.submit()}
                  >
                    บันทึก
                  </Button>
                  <Button
                    icon={<FileExcelOutlined />}
                    loading={loading}
                    style={{
                      borderRadius: 8,
                      fontWeight: 600,
                      color: "#16a34a",
                      borderColor: "#16a34a",
                    }}
                    onClick={handleExportExcel}
                  >
                    ออก Excel
                  </Button>
                </Space>
              </Col>
            </Row>

            <Form
              form={form}
              onValuesChange={handleFormValuesChange}
              onFinish={confirmInsert}
              onFinishFailed={() =>
                notification.error({
                  message: "กรุณาตรวจสอบข้อมูลให้ครบถ้วน !",
                  duration: 4,
                })
              }
            >
              <Table
                size="large"
                rowKey={(r) => r.key}
                columns={columns}
                dataSource={arrayTable}
                pagination={false}
                bordered
                style={{ marginTop: 8 }}
                rowSelection={{
                  selectedRowKeys,
                  onChange: (keys, rows) => {
                    setSelectedRowKeys(keys);
                    setSelectedRows(rows);
                  },
                }}
                footer={() => (
                  <div
                    style={{
                      textAlign: "right",
                      fontWeight: 600,
                      fontSize: 16,
                    }}
                  >
                    จำนวนทั้งหมด {arrayTable.length} รายการ
                  </div>
                )}
              />
            </Form>

            <FloatButton.BackTop
              target={() => scrollRef.current}
              visibilityHeight={80}
              style={{ right: 16, bottom: 16 }}
            />
          </div>
        ) : (
          <div>
            <Calendar
              value={calendarValue}
              mode={calendarMode}
              onSelect={handleSelect}
              onPanelChange={(v, m) => {
                setCalendarValue(v);
                setCalendarMode(m);
              }}
              cellRender={cellRender}
              headerRender={({ value, type, onChange, onTypeChange }) => {
                const cur = value.clone();
                const years = Array.from(
                  { length: 11 },
                  (_, i) => dayjs().year() - 5 + i,
                );
                const months = Array.from({ length: 12 }, (_, i) => ({
                  value: i,
                  label: dayjs().month(i).format("MMM"),
                }));
                return (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      justifyContent: "flex-end",
                      padding: 8,
                    }}
                  >
                    <Select
                      size="small"
                      value={cur.year()}
                      options={years.map((y) => ({ value: y, label: y }))}
                      onChange={(y) => onChange(cur.year(y))}
                    />
                    <Select
                      size="small"
                      value={cur.month()}
                      options={months}
                      onChange={(m) => onChange(cur.month(m))}
                      disabled={type === "year"}
                    />
                    <Segmented
                      size="small"
                      value={type === "year" ? "Year" : "Month"}
                      options={["Month", "Year"]}
                      onChange={(v) =>
                        onTypeChange(v === "Year" ? "year" : "month")
                      }
                    />
                  </div>
                );
              }}
            />
            <Row justify="end" style={{ marginTop: 8 }}>
              <Button
                icon={<ArrowRightOutlined />}
                size="large"
                type="primary"
                danger
                ghost
                disabled={loading}
                onClick={() => {
                  loadByDate(calendarValue);
                  setBtnCheckCalendar(true);
                }}
              >
                ข้าม
              </Button>
            </Row>
          </div>
        )}
        {isModal && (
          <BillAfterCourtOldData
            open={isModal}
            close={() => setIsModal(false)}
            dataDefault={dataRecord}
          />
        )}
      </Modal>
    </Spin>
  );
};

export default AddEmsAndExport;
